import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import SubscriptionSection from './SubscriptionSection';
import './Profile.css';

// Function to format the income range
const formatIncomeRange = (range) => {
  if (!range) return 'Not provided';
  if (range === "1000000+" || range === "1000000") {
    return "$1M+";
  }
  const [low, high] = range.split("-");
  const formattedLow = `$${(Number(low) / 1000).toFixed()}K`;
  const formattedHigh = high === "1000000" ? "$1M" : `$${(Number(high) / 1000).toFixed()}K`;
  return `${formattedLow} - ${formattedHigh}`;
};

const Profile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [isPersonalInfoExpanded, setIsPersonalInfoExpanded] = useState(false);

  // Username editing state
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isSavingUsername, setIsSavingUsername] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axiosInstance.get(
          userId ? `/profile/${userId}/` : `/profile/`
        );
        if (response.data) {
          setProfile(response.data);
        } else {
          setError('No profile data received');
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setError('Please log in to view this profile.');
        } else if (err.response?.status === 404) {
          setError('Profile not found.');
        } else {
          setError('Failed to load profile. Please try again later.');
        }
        console.error('Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [userId]);

  const togglePersonalInfo = () => {
    setIsPersonalInfoExpanded(!isPersonalInfoExpanded);
  };

  const handleEditUsername = () => {
    setNewUsername(profile.username);
    setIsEditingUsername(true);
    setUsernameError('');
  };

  const handleCancelEdit = () => {
    setIsEditingUsername(false);
    setNewUsername('');
    setUsernameError('');
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      setUsernameError('Username cannot be empty');
      return;
    }

    if (newUsername === profile.username) {
      setIsEditingUsername(false);
      return;
    }

    setIsSavingUsername(true);
    setUsernameError('');

    try {
      // Use the new username update endpoint
      const response = await axiosInstance.put('/update-username/', {
        username: newUsername.trim()
      });

      // Update only the username in the existing profile state
      setProfile(prevProfile => ({
        ...prevProfile,
        username: response.data.username
      }));

      setIsEditingUsername(false);
      setNewUsername('');
    } catch (err) {
      if (err.response?.data?.error) {
        setUsernameError(err.response.data.error);
      } else if (err.response?.data?.username) {
        setUsernameError(err.response.data.username[0] || 'Username already exists');
      } else {
        setUsernameError('Failed to update username. Please try again.');
      }
      console.error('Error updating username:', err);
    } finally {
      setIsSavingUsername(false);
    }
  };

  if (error) {
    return (
      <div className="profile-container error">
        <p>{error}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container loading">
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <h2>Member Information</h2>

      {/* Personal Information Section - Collapsible */}
      <div className="personal-info-section">
        <div
          className="personal-info-header"
          onClick={togglePersonalInfo}
        >
          <h3>Personal Information</h3>
          <span className="toggle-icon">
            {isPersonalInfoExpanded ? '−' : '+'}
          </span>
        </div>

        <div className={`personal-info-content ${isPersonalInfoExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="profile-details">
            {/* Username with edit functionality */}
            <div className="username-section">
              <strong>Username:</strong>{' '}
              {isEditingUsername ? (
                <div className="username-edit-form">
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="username-input"
                    placeholder="Enter new username"
                    disabled={isSavingUsername}
                  />
                  <div className="username-edit-buttons">
                    <button
                      onClick={handleSaveUsername}
                      disabled={isSavingUsername}
                      className="save-button"
                    >
                      {isSavingUsername ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSavingUsername}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                  </div>
                  {usernameError && (
                    <div className="username-error">{usernameError}</div>
                  )}
                </div>
              ) : (
                <span className="username-display">
                  {profile.username || 'Not provided'}
                  <button
                    onClick={handleEditUsername}
                    className="edit-username-button"
                    title="Edit username"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon2" viewBox="0 0 24 24">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </button>
                </span>
              )}
            </div>

            <p><strong>Email:</strong> {profile.email || 'Not provided'}</p>
            <p><strong>Phone Number:</strong> {profile.phone_number || 'Not provided'}</p>
            <p><strong>Zip Code:</strong> {profile.primary_zip_code || 'Not provided'}</p>
            <p><strong>Age:</strong> {profile.age || 'Not provided'}</p>
            <p>
              <strong>Marital Status:</strong>{' '}
              <span className="capitalize">{profile.marital_status || 'Not provided'}</span>
            </p>
            <p><strong>Number of Children:</strong> {profile.number_of_children || 'Not provided'}</p>
            <p>
              <strong>Household Income:</strong>{' '}
              {profile.household_income ? formatIncomeRange(profile.household_income) : 'Not provided'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Subscription Section */}
      <div className="membership-section">
        <SubscriptionSection />
      </div>
    </div>
  );
};

export default Profile;